import { addTokenToHeaders, get, post } from "../utils/fetch-api";
import { OrderDTO } from "../data/types";

interface OrderResponse {
  url: string;
  message: string;
  status: number;
}

export enum PromoCodeType {
  PERCENTAGE = "PERCENTAGE",
  FIXED = "FIXED",
}

export interface PromoCode {
  valid: boolean;
  value: number;
  type: PromoCodeType;
  message: string;
  codeText: string;
  startDate?: string | null; // ISO date YYYY-MM-DD; if absent, code works from any start
  endDate?: string | null; // ISO date YYYY-MM-DD; if absent, code works until any end
}

export function isSiteClosed(): Promise<string> {
  return get<string>("/api/utils/isSiteClosed");
}

export interface OpeningHoursData {
  openingHours: {
    [key: string]: { startHour: number; endHour: number };
  };
  overrideStatus: {
    [key: string]: {
      isClosed?: boolean;
      startHour?: number;
      endHour?: number;
    };
  };
}

export interface DateTimeWorkingHoursCheck {
  isWithinHours: boolean;
  isClosed: boolean;
  startHour: number | null;
  endHour: number | null;
}

export function getOpeningHoursData(): Promise<OpeningHoursData> {
  return get<OpeningHoursData>("/api/utils/openingHours");
}

export function checkDateTimeWorkingHours(
  dateTime: string
): Promise<DateTimeWorkingHoursCheck> {
  return post<DateTimeWorkingHoursCheck>(
    "/api/utils/checkDateTimeWorkingHours",
    { dateTime }
  );
}

export interface DeliveryStatus {
  isDeliveryDisabled: boolean;
}

export function getDeliveryStatus(): Promise<DeliveryStatus> {
  return get<DeliveryStatus>("/api/utils/deliveryStatus");
}

export function enableDeliveryApi(token: string): Promise<DeliveryStatus> {
  const headers = addTokenToHeaders(token);
  return post<DeliveryStatus>("/api/utils/enableDelivery", {}, headers);
}

export function disableDeliveryApi(token: string): Promise<DeliveryStatus> {
  const headers = addTokenToHeaders(token);
  return post<DeliveryStatus>("/api/utils/disableDelivery", {}, headers);
}

export function order(data: OrderDTO): Promise<OrderResponse> {
  return post<OrderResponse>("/api/order", data);
}

export function checkPromoCode(promoCode: string): Promise<PromoCode> {
  return post<PromoCode>("/api/utils/checkPromoCode", { promoCode: promoCode });
}

export function loginApi(data: {
  username: string;
  password: string;
}): Promise<{ token: string }> {
  return post<{ token: string }>("/api/login", data);
}

export function openSiteToday(token: string): Promise<void> {
  const headers = addTokenToHeaders(token);
  return post<void>("/api/utils/openToday", {}, headers);
}

export function resetOverrides(token: string): Promise<void> {
  const headers = addTokenToHeaders(token);
  return post<void>("/api/utils/resetOverrides", {}, headers);
}

export function closeSiteToday(token: string): Promise<void> {
  const headers = addTokenToHeaders(token);
  return post<void>("/api/utils/closeToday", {}, headers);
}

export function getHashed(data: { toHash: string }): Promise<string> {
  return post<string>("/api/getHashed", data);
}

export function getPromoCodes(token: string): Promise<PromoCode[]> {
  const headers = addTokenToHeaders(token);
  return get<PromoCode[]>("/api/utils/promoCodes", headers);
}

export interface OrderItem {
  name: string;
  category: string;
  price: number;
  count: number;
  summary_price: number;
}

export interface Order {
  id: number;
  session_id: string;
  first_name: string;
  email: string;
  phone: string;
  delivery_option: string;
  payment_option: string;
  address: string | null;
  postal_code: string | null;
  city: string | null;
  promo_code: PromoCode | string | null;
  products: OrderItem[];
  products_price: number;
  discount: number;
  delivery_price: number;
  total_price: number;
  status: string;
  notes: string | null;
  comment: string | null;
  persons_count: number | null;
  call_me: boolean | null;
  delivery_time_option: string | null;
  scheduled_at: string | null;
  created_at: string;
}

export function getOrders(token: string): Promise<Order[]> {
  const headers = addTokenToHeaders(token);
  return get<Order[]>("/api/orders", headers);
}

export function runMigrations(password: string): Promise<{ output: string }> {
  return post<{ output: string }>("/api/migrate", { password });
}
